import { createSelector } from 'reselect';
import { RootState } from 'src/store';
import {
    buildSizeChartFromProduct,
    buildSizeChartFromLinkedAttributes,
    resolveBodyWidthMm,
    resolveBodyLengthMm,
    resolveLinkedBodyWidthMm,
    resolveLinkedBodyLengthMm,
} from 'src/pages/editor/components/decorationSpecs/productPropertySizeChart';
import { getProductDetails } from './getSurfaceProperties';

const getDecorationSpecsState = (state: RootState) => state.pages.editor.decorationSpecs;

export const getProductModel = createSelector([getDecorationSpecsState], (state) => state.productModel);

// TODO(linked-product-fallback): remove `getLinkedSizeAttributes` and both fallback branches
// below alongside the rest of this fallback — see `linkedMerchandisedProductClient.ts`'s module
// doc.
/** Width/Length resolved via the linked-merchandised-product fallback (see
 * `productPropertySizeChart.ts`'s module doc) — only ever populated when the CIM's own product
 * definition doesn't declare Width/Length itself. */
const getLinkedSizeAttributes = createSelector([getDecorationSpecsState], (state) => state.linkedSizeAttributes);

/** The designer-entered override, if any — see `BodyMeasurementOverrideType`'s own doc comment.
 * The final, highest-priority tier above both `getLinkedSizeAttributes` and the CIM's own
 * properties, consumed directly by `getBodyMeasurements` below (not threaded into `getSizeChart`
 * — the size chart table stays a plain reflection of what the CIM/linked-product sources say, so
 * a designer can see the override next to the value it's replacing rather than it silently
 * disappearing into an existing row; see `decorationSpecs.tsx`'s own override UI).
 *
 * Exported (unlike `getLinkedSizeAttributes`) for `scene/saga.ts`'s `getScene()` to collect when
 * saving — the raw value, not filtered to the current size the way
 * `getBodyMeasurementOverrideForCurrentSize` below is for display: a saved scene should keep
 * whatever override was entered even across an in-session Size change the designer hasn't
 * resolved yet, exactly like `getAllGarmentMarkers`/`getAllGarmentPlacements` save their state
 * unconditionally rather than filtering to what currently applies. */
export const getBodyMeasurementOverride = createSelector(
    [getDecorationSpecsState],
    (state) => state.bodyMeasurementOverride,
);

export const getSizeChart = createSelector(
    [getProductModel, getProductDetails, getLinkedSizeAttributes],
    (productModel, surfaceDetails, linkedSizeAttributes) => {
        // Only derive size chart if a product is actually selected/added into the editor (isReady true)
        if (!surfaceDetails?.isReady) {
            return null;
        }
        const selections = surfaceDetails.merchantVariables ?? {};
        const ownSizeChart = buildSizeChartFromProduct(productModel, selections);
        // Fall back to the linked merchandised product's Width/Length when the CIM's own
        // definition has neither — a single-size row, not a full table (see
        // `buildSizeChartFromLinkedAttributes`'s own doc comment for why, including why it's
        // guarded against the *current* Size here rather than trusting the saga's own timing).
        return ownSizeChart ?? buildSizeChartFromLinkedAttributes(linkedSizeAttributes, selections.Size ?? '');
    },
);

/** The designer-entered override when it applies to the current size, `null` otherwise — the
 * same staleness guard `buildSizeChartFromLinkedAttributes`/`resolveLinkedBodyWidthMm` already
 * use for `linkedSizeAttributes`, so an override entered for a since-changed Size doesn't get
 * silently applied to whatever size is selected now. Both `getBodyMeasurements` (below) and the
 * UI's "is this an override" display (`decorationSpecs.tsx`) build on this single check, rather
 * than each re-deriving their own `override?.size === size` comparison. */
export const getBodyMeasurementOverrideForCurrentSize = createSelector(
    [getProductDetails, getBodyMeasurementOverride],
    (surfaceDetails, override) => {
        const size = surfaceDetails?.merchantVariables?.Size ?? '';
        if (!size || override?.size !== size) {
            return null;
        }
        return override;
    },
);

export const getBodyMeasurements = createSelector(
    [getProductModel, getProductDetails, getLinkedSizeAttributes, getBodyMeasurementOverrideForCurrentSize],
    (productModel, surfaceDetails, linkedSizeAttributes, override) => {
        if (!surfaceDetails?.isReady) {
            return {
                bodyWidthMm: undefined,
                bodyLengthMm: undefined,
            };
        }
        const selections = surfaceDetails.merchantVariables ?? {};
        const size = selections.Size ?? '';
        return {
            bodyWidthMm:
                override?.widthMm ??
                resolveBodyWidthMm(productModel, selections, size) ??
                resolveLinkedBodyWidthMm(linkedSizeAttributes, size),
            bodyLengthMm:
                override?.lengthMm ??
                resolveBodyLengthMm(productModel, selections, size) ??
                resolveLinkedBodyLengthMm(linkedSizeAttributes, size),
        };
    },
);

const getDecorationSpecs = createSelector(
    [getDecorationSpecsState, getSizeChart, getBodyMeasurements],
    (decorationSpecsState, sizeChart, bodyMeasurements) => ({
        ...decorationSpecsState,
        sizeChart,
        bodyWidthMm: bodyMeasurements.bodyWidthMm,
        bodyLengthMm: bodyMeasurements.bodyLengthMm,
    }),
);

export default getDecorationSpecs;
